import { Inject, Injectable, PLATFORM_ID } from '@angular/core'
import { mainDomain } from '../common/utilities/constants'
import { CookieService } from 'ngx-cookie'
import { sha256 } from 'js-sha256'
import { isPlatformBrowser } from '@angular/common'
import { UserAuthService } from './user-auth.service'
import { Observable } from 'rxjs/internal/Observable'
import { getItmParamsFromStorage } from '../common/utilities/utm-params-utils'
import { gtmConst } from '../common/utilities/trackingConstants'
declare global {
  interface Window {
    dataLayer: any[]
  }
}
@Injectable({
  providedIn: 'root'
})
export class GtmTrackingService {
  constructor (private cookieService: CookieService, @Inject(PLATFORM_ID) public platformId: Object, private authService: UserAuthService) { }
  setOrGetClientTracking () {
    let clientTracking: any = this.cookieService.get('client_tracking')
    if (clientTracking) {
      clientTracking = JSON.parse(clientTracking)
      if (clientTracking?.userstatus === 'logged in' && (!clientTracking?.old_user_id || clientTracking?.old_user_id === 'na')) {
        clientTracking.old_user_id = this.getGaValue()
        this.cookieService.put('client_tracking', JSON.stringify(clientTracking), { domain: mainDomain, path: '/', secure: true, sameSite: 'none' }
        )
      }
    } else {
      clientTracking = {
        userstatus: 'anonymous',
        old_user_id: this.getGaValue()
      }
      this.cookieService.put('client_tracking', JSON.stringify(clientTracking), { domain: mainDomain, path: '/', secure: true, sameSite: 'none' }
      )
    }
  }

  getCookieValue (): string {
    const optanonConsent = document.cookie.split('; ').find(cookie => cookie.startsWith('OptanonConsent='))
    if (!optanonConsent) {
      return ''
    }
    return optanonConsent
  }

  getConsentGroupsMatch () {
    const cookieConsent = this.getCookieValue()
    const consentGroupsMatch = cookieConsent.match(/groups=([^&]*)/)
    const finalValue: string[] = []
    if (consentGroupsMatch?.length) {
      decodeURIComponent(consentGroupsMatch[1]).split(',').forEach(data => {
        if (data?.includes(':1')) {
          finalValue.push(data?.split(':')[0])
        }
      })
    }
    return finalValue?.length ? ',' + finalValue.join(',') + ',' : ',C0001,C0004,C0003,C0002,'
  }

  setClientTrackingWithLoggedInUser (userEmail?: any): Promise<void> {
    return new Promise((resolve) => {
      let clientTracking: any = this.cookieService.get('client_tracking')
      if (clientTracking) {
        clientTracking = JSON.parse(clientTracking)
      } else {
        clientTracking = {}
      }
      if (userEmail) {
        const hashedEmail = sha256(userEmail)
        if (clientTracking.userId && clientTracking.userId !== hashedEmail) {
          clientTracking.old_user_id = clientTracking?.userId
        }
        clientTracking.userId = hashedEmail
        clientTracking.userstatus = 'logged in'
        this.cookieService.put('client_tracking', JSON.stringify(clientTracking), { domain: mainDomain, path: '/', secure: true, sameSite: 'none' })
      } else {
        this.setUserAsLoggedOut(clientTracking)
      }
      resolve()
    })
  }

  setUserAsLoggedOut (clientTracking: any) {
    if (clientTracking?.userstatus === 'logged in' && clientTracking?.userstatus !== 'anonymous') {
      clientTracking.userstatus = 'logged out'
      this.cookieService.put('client_tracking', JSON.stringify(clientTracking), {
        domain: mainDomain, path: '/', secure: true, sameSite: 'none'
      })
    }
  }
  getGaValue () {
    let userId: any = ''
    const gaValue = this.cookieService.get('_ga')
    if (gaValue) {
      userId = this.extractGaValue(gaValue)
    }
    return userId
  }

  extractGaValue (gaCookieValue: string): string | null {
    if (!gaCookieValue) return null
    const parts = gaCookieValue.split('.')
    if (parts.length >= 4 && parts[2] && parts[3]) {
      return parts[2] + '.' + parts[3]
    }
    return null
  }
  waitForGaUserId (): Observable<string> {
    return new Observable((observer) => {
      const intervalId = setInterval(() => {
        const ga4UserId = this.cookieService.get('_ga')
        if (ga4UserId) {
          clearInterval(intervalId)
          observer.next(ga4UserId)
          observer.complete()
        }
      }, 100)
    })
  }
  logGaClickEvents (eventCategory: string, pagename: string, pageReferrer: string, eventAction?: string, eventLabel?: string, feature?: string, step?: string, userEmail?: string) {
    let trackingDetails: any = this.cookieService.get('client_tracking')
     if (typeof trackingDetails === 'string') {
      trackingDetails = JSON.parse(trackingDetails)
    }
    if (isPlatformBrowser(this.platformId)) {
      const dataLayerEvent: any = {
        event: 'events_ga4',
        'event category': eventCategory || 'na',
        'event action': eventAction || 'na',
        'event label': eventLabel || 'na',
        feature: feature || 'na',
        websitename: 'one_account',
        pagename: pagename || 'na',
        page_referrer: pageReferrer || 'na',
        language: 'English',
        userstatus: trackingDetails?.userstatus || null,
        old_user_id: trackingDetails?.old_user_id || null,
        OnetrustActiveGroups: this.getConsentGroupsMatch()
      }
      if (eventCategory?.toLowerCase()?.includes('signin') || eventCategory?.toLowerCase()?.includes('signup')) {
        dataLayerEvent.feature = gtmConst.feature.bxBooking
        dataLayerEvent.test_type = this.getGaTestType()
        dataLayerEvent.step = step || 'na'
      }
      const resolvedUserId = userEmail ? sha256(userEmail) : trackingDetails?.userId
      if (resolvedUserId) {
        dataLayerEvent.user_id = resolvedUserId
      }
      this.appendItmParams(dataLayerEvent)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(dataLayerEvent)
    }
  }

  getGaTestType (): string {
    let testModule = ''
    let testCategory = ''
    const referrerUrl = decodeURIComponent(this.authService.getCookie('referrerUrl') || '')
    const queryString = referrerUrl.includes('?') ? referrerUrl.split('?')[1] : ''
    if (queryString) {
      const params = new URLSearchParams(queryString)
      testModule = params.get('tm') || params.get('testModule') || ''
      testCategory = params.get('tc') || params.get('testCategory') || ''
    }

    if (!testModule || !testCategory) return 'na'
    const module = String(testModule).toLowerCase()
    const category = String(testCategory).toLowerCase()
    if (category.includes('ukvi')) {
      return `ielts_for_ukvi_(${module})`.replace(/\s+/g, '_')
    }
    if (category.includes('life_skills') || category.includes('life skills')) {
      return `life_skills_${module}`.replace(/\s+/g, '_')
    }
    return `ielts_${module}`.replace(/\s+/g, '_')
  }

  logGaPageview (pagename: string) {
    let trackingDetails: any = this.cookieService.get('client_tracking')
     if (typeof trackingDetails === 'string') {
      trackingDetails = JSON.parse(trackingDetails)
    }
    if (isPlatformBrowser(this.platformId)) {
      const dataLayerEvent: any = {
          event: 'pageview',
          websitename: 'one_account',
          pagename: pagename || 'na',
          language: 'English',
          userstatus: trackingDetails?.userstatus || null,
          old_user_id: trackingDetails?.old_user_id || null,
          OnetrustActiveGroups: this.getConsentGroupsMatch()
      }
      if (trackingDetails?.userId) {
        dataLayerEvent.user_id = trackingDetails.userId
      }
      this.appendItmParams(dataLayerEvent)
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(dataLayerEvent)
    }
  }

  private appendItmParams (dataLayerEvent: any): void {
    const itmParams = getItmParamsFromStorage()
    if (itmParams) {
      dataLayerEvent.itm_source = itmParams.itm_source || 'na'
      dataLayerEvent.itm_medium = itmParams.itm_medium || 'na'
      dataLayerEvent.itm_campaign = itmParams.itm_campaign || 'na'
      dataLayerEvent.itm_content = itmParams.itm_content || 'na'
    }
  }
}
